import RadioButton from "@ebsco-ui/radio-button";
import DateRange, { DateRangeProps } from "@ebsco-ui/date-range";
import { createBemBlockBuilder } from "@ebsco-ui/utils";

import "@ebsco-ui/date-range/date-range.scss";
import "./date-filters.scss";

type QuickFilterValue = string | number;

export interface FilterData {
  selectedFilter: QuickFilterValue;
  customRange: {
    minDate: string;
    maxDate: string;
  };
}

interface QuickFilterObject {
  label: string;
  value: QuickFilterValue;
}

type ChangeEventHandler = (filterData: FilterData) => void;
export interface DateFiltersProps {
  className?: string;
  i18n: {
    groupTitle: string;
    minDateLabel: string;
    maxDateLabel: string;
  };
  dateRange: {
    minDate: DateRangeProps["minDate"];
    maxDate: DateRangeProps["maxDate"];
  };
  isSharedError: boolean;
  quickFilters: Array<QuickFilterObject>;
  onChange: ChangeEventHandler;
  selectedFilter: QuickFilterValue;
  showDateRange: boolean;
  placeholder?: DateRangeProps["placeholder"];
  uniqueName?: string;
}

const baseClassName = "eb-date-filters";

export const DateFilters = (props: DateFiltersProps) => {
  const {
    className,
    dateRange,
    i18n: { groupTitle, ...dateRangeI18n },
    isSharedError,
    quickFilters,
    placeholder,
    selectedFilter,
    showDateRange,
    uniqueName = "date-filter",
    onChange,
    ...rest
  } = props;

  const getBlocksWith = createBemBlockBuilder([baseClassName, className]);

  const handleChange = (value: string | number) => {
    onChange({
      selectedFilter: value,
      // send back for consistency
      customRange: {
        minDate: dateRange.minDate.value,
        maxDate: dateRange.maxDate.value,
      },
    });
  };

  const handleCustomRangeChange: DateRangeProps["onChange"] = (
    minDate,
    maxDate,
  ) => {
    onChange({
      selectedFilter,
      customRange: { minDate, maxDate },
    });
  };

  const dateRangeUniqueName = `${uniqueName}-custom-range`;

  return (
    <div className={getBlocksWith()}>
      <div
        className={getBlocksWith("__group")}
        /* Keep role and aria-label as defaults for backward compatibility */
        role="group"
        aria-label={groupTitle}
        data-auto="quick-date-filters-group"
        {...rest}
      >
        {quickFilters.map(({ label, value }) => (
          <RadioButton
            key={value}
            label={label}
            name={uniqueName}
            value={value}
            onChange={(ev) =>
              handleChange(
                // input converts all values to string, we have to convert it back to number to make the component work correctly
                typeof value === "number"
                  ? Number(ev.target.value)
                  : ev.target.value,
              )
            }
            checked={selectedFilter === value}
            className={getBlocksWith("__filter")}
            data-auto="quick-date-filter"
          />
        ))}
      </div>
      {showDateRange && (
        <DateRange
          i18n={dateRangeI18n}
          minDate={dateRange.minDate}
          maxDate={dateRange.maxDate}
          isSharedError={isSharedError}
          onChange={handleCustomRangeChange}
          placeholder={placeholder}
          uniqueName={dateRangeUniqueName}
          className={getBlocksWith("-date-range")}
        />
      )}
    </div>
  );
};

export default DateFilters;
